import { useTheme } from "@mui/material";
import { createSxStyles } from "@common/createSxStyles";

// Wide enough that the arrow clears the trailing tooltip icon the fields render for themselves.
const arrowColumnWidth = 48;
// Room under each field for its citation link.
const citationSlotHeight = 2.5;

export function useContractAiUpdateStyles() {
    const theme = useTheme();

    return createSxStyles({
        // No scroller of its own: the pane's content scrolls, which is what puts the pane's inset between the
        // rows and the scrollbar. A flex column so a step can hand its spare height to what it centres.
        body: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
        },
        analysingSpinner: {
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        /**
         * In the pane's fixed content rather than the scroll area: there it could only ever be a sticky bar
         * with rows showing through the space around it. It shares the content inset with the rows below, so
         * the three columns line up without either adding side padding of its own.
         */
        columnHeadings: {
            display: "grid",
            gridTemplateColumns: `minmax(0, 1fr) ${arrowColumnWidth}px minmax(0, 1fr)`,
            alignItems: "center",
            gap: 1,
            paddingBottom: theme.spacing(1),
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
        // minmax(0, 1fr) rather than 1fr: a grid track defaults to min-width auto, so a column would refuse
        // to shrink below its content and push the field's own tooltip icon out past the pane instead.
        row: {
            display: "grid",
            gridTemplateColumns: `minmax(0, 1fr) ${arrowColumnWidth}px minmax(0, 1fr)`,
            alignItems: "start",
            gap: 1,
        },
        column: {
            minWidth: 0,
        },
        arrowCell: {
            display: "flex",
            justifyContent: "center",
            // Centred on the row rather than held a fixed distance from its top: a multiline box, a chip box
            // and a plain input each start their value at their own offset, so any constant sat at a
            // different height on every row. The side padding keeps the arrow off the trailing tooltip icon
            // each field renders at its own right edge. The bottom padding matches the citation room under the
            // fields, so the arrow centres on the field box rather than on box and citation together.
            alignSelf: "center",
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            paddingBottom: theme.spacing(citationSlotHeight),
        },
        // A disabled input greys its text out, which on a whole read-only column reads as unavailable
        // rather than as the value the contract currently holds.
        readOnlyColumn: {
            "& .Mui-disabled": {
                WebkitTextFillColor: theme.palette.text.primary,
            },
        },
        /**
         * Owns the vertical rhythm outright. The fields are rendered with noMargin so their own 16px/8px
         * does not stack with this padding and leave the citation link floating in the gap between them.
         */
        fieldWrapper: {
            position: "relative",
            paddingBottom: theme.spacing(citationSlotHeight),
            "&:hover [data-citation], &:focus-within [data-citation]": {
                opacity: 1,
            },
            // The group companies field centres its checkbox and takes its width from the window, which in a
            // review column leaves it floating mid-column with its label wrapped. Corrected here rather than
            // in the field, which looks right everywhere the form is full width.
            "& [data-group-companies-field]": {
                alignItems: "flex-start",
                "& > *": {
                    width: "100%",
                },
            },
        },
        // Hidden until the pointer is on the field, so a column of links does not compete with the values
        // themselves. Kept in the layout rather than unmounted, so nothing moves as it fades in.
        citationSlot: {
            position: "absolute",
            left: 0,
            right: 0,
            bottom: theme.spacing(0.25),
            display: "flex",
            justifyContent: "flex-end",
            opacity: 0,
            transition: "opacity 0.15s ease",
        },
        // Every other field labels itself with a floating input label; a header here shouted over them.
        partiesLabel: {
            fontSize: theme.typography.pxToRem(12),
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(0.5),
        },
        partyList: {
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(1),
        },
        partyRow: {
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1),
            padding: theme.spacing(1),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
        },
        // The citation shares the name's line, so a party with one is no taller than the same party in the
        // current column, which has none. A long name gives way with an ellipsis rather than wrapping the
        // citation onto a line of its own.
        partyNameLine: {
            display: "flex",
            alignItems: "center",
            columnGap: theme.spacing(1.5),
            minWidth: 0,
        },
        partyCitation: {
            flexShrink: 0,
        },
        // The label is where the name lives, so it has to be allowed to shrink for the ellipsis to show.
        partyCheckboxLabel: {
            minWidth: 0,
            marginRight: 0,
            "& .MuiFormControlLabel-label": {
                minWidth: 0,
            },
        },
        // A small icon button or checkbox is taller than a line of 14px text; pulled into the line so the
        // proposed box is the same height as the current one beside it.
        partyInlineControl: {
            marginTop: theme.spacing(-0.75),
            marginBottom: theme.spacing(-0.75),
        },
        // The inputs the other fields render carry their value at 14px; a default Typography beside them
        // reads as a heading.
        partyName: {
            fontSize: theme.typography.pxToRem(14),
        },
        partyDetail: {
            fontSize: theme.typography.pxToRem(12),
            color: theme.palette.text.secondary,
        },
        // The party list is not an input, so it draws the helper-text line an input would draw for itself.
        partyListError: {
            fontSize: theme.typography.pxToRem(12),
            color: theme.palette.error.main,
            marginTop: theme.spacing(0.5),
        },
        /**
         * The arrow column the field rows use, reached from inside the left column: the party boxes fill the
         * column, so the control has to leave them to line up with every other row's arrow. Half the arrow
         * column plus the grid gap puts it on that column's centre line.
         */
        partyToggle: {
            position: "absolute",
            left: "100%",
            top: "50%",
            marginLeft: `calc(${theme.spacing(1)} + ${arrowColumnWidth / 2}px)`,
            transform: "translate(-50%, -50%)",
        },
    });
}
